import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetJobsQuery,
  useToggleJobStatusMutation,
  useDeleteJobMutation,
} from "../../../redux/api/CareersApi/careersApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaUserTie,
  FaCheckCircle,
  FaTrash,
} from "react-icons/fa";
import Swal from "sweetalert2";

const Jobs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "display_order";
  const order = searchParams.get("order") || "asc";
  const limit = Number(searchParams.get("limit")) || 10;

  const [searchTerm, setSearchTerm] = useState(search);

  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1,
          limit,
          search: searchTerm,
          sortBy,
          order,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, limit, sortBy, order, search, setSearchParams]);

  const { data, isLoading } = useGetJobsQuery({
    page,
    limit,
    search,
    sortBy,
    order,
  });

  const [toggleStatus] = useToggleJobStatusMutation();
  const [deleteJob] = useDeleteJobMutation();

  const jobs = data || [];

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  const handleToggle = async (id) => {
    try {
      await toggleStatus(id).unwrap();
    } catch (error) {
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteJob(id).unwrap();
        Swal.fire("Deleted!", "Job has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to delete job", "error");
      }
    }
  };

  const activeCount = jobs.filter((s) => s.status).length;

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaUserTie className="text-primary me-2" />
            Jobs Management
          </h2>
          <p className="text-muted mb-0">
            Manage your career opportunities and openings
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => navigate("/admin/applications")}
            className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
          >
            View Applications
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate("/admin/add-job")}
            className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm rounded-pill fw-semibold"
          >
            <FaPlus /> Add New Job
          </Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
            <div className="d-flex align-items-center">
              <div className="bg-primary-soft text-primary p-3 rounded-circle me-3">
                <FaUserTie size={20} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{jobs.length}</h4>
                <div className="text-muted small fw-semibold">
                  Total Job Openings
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm rounded-4 bg-white p-3 h-100">
            <div className="d-flex align-items-center">
              <div className="bg-success-soft text-success p-3 rounded-circle me-3">
                <FaCheckCircle size={20} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{activeCount}</h4>
                <div className="text-muted small fw-semibold">
                  Active Openings
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center">
            <Col md={12}>
              <InputGroup className="bg-light rounded-pill border-0 px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search jobs by title or category..."
                  className="bg-transparent border-0 shadow-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="bg-light text-muted small text-uppercase">
              <tr>
                <th className="ps-4">Job Title</th>
                <th>Category</th>
                <th>Type</th>
                <th className="text-center">Order</th>
                <th className="text-center">Status</th>
                <th className="text-end pe-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id}>
                  <td className="ps-4 py-3">
                    <div className="fw-bold text-dark">{job.title}</div>
                    <div className="text-muted extra-small">{job.location}</div>
                  </td>
                  <td>
                    <Badge bg="info" className="text-white">
                      {job.jobCategory?.name || job.category}
                    </Badge>
                  </td>
                  <td>{job.jobType}</td>
                  <td className="text-center">
                    <Badge bg="secondary" pill>
                      {job.display_order}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <Badge
                      bg={job.status ? "success" : "danger"}
                      className="cursor-pointer"
                      onClick={() => handleToggle(job.id)}
                    >
                      {job.status ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() => navigate(`/admin/edit-job/${job.id}`)}
                        className="shadow-sm border"
                      >
                        <FaEdit className="text-primary" />
                      </Button>
                      <Button
                        variant="white"
                        size="sm"
                        onClick={() => handleDelete(job.id)}
                        className="shadow-sm border"
                      >
                        <FaTrash className="text-danger" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && !isLoading && (
                <tr>
                  <td colSpan="6" className="text-center py-5 text-muted">
                    No jobs found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default Jobs;
